import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";
import {
	NotificationPayload,
	VendingStepData,
	NotificationConnectionState,
	NotificationCallback,
	VendingStatusCallback,
	ConnectionStateCallback,
	BroadcastNotificationSubType,
} from "../../model/notifications/notifications";
import { store } from "../../stores/store";
import { connectionStateChanged, notificationReceived, vendingStatusReceived } from "../../stores/slices/notification-slice";
import { broadcastNotificationReceived } from "../../stores/slices/broadcast-notification-slice";
import { logOut } from "../../stores/slices/current-user-slice";

class NotificationHubManager {
	private hubConnection: HubConnection | null = null;
	private reconnectAttempts = 0;
	private readonly maxReconnectAttempts = 5;
	private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private stopped = false;

	private notificationSubscribers = new Set<NotificationCallback>();
	private vendingStatusSubscribers = new Set<VendingStatusCallback>();
	private connectionStateSubscribers = new Set<ConnectionStateCallback>();

	async initialize(url: string, tokenFactory: () => Promise<string>): Promise<void> {
		const state = this.hubConnection?.state;
		if (state === HubConnectionState.Connected || state === HubConnectionState.Connecting) {
			console.warn("SignalR connection already active, skipping initialize");
			return;
		}

		this.stopped = false;

		this.hubConnection = new HubConnectionBuilder()
			.withUrl(url, {
				// Called before every connect and reconnect attempt — always gets a fresh token
				accessTokenFactory: tokenFactory,
			})
			.withAutomaticReconnect({
				nextRetryDelayInMilliseconds: retryContext => {
					if (retryContext.previousRetryCount === 0) return 0;
					if (retryContext.previousRetryCount === 1) return 2000;
					if (retryContext.previousRetryCount === 2) return 10000;
					if (retryContext.previousRetryCount === 3) return 30000;
					return 60000;
				},
			})
			.configureLogging(LogLevel.Warning)
			.build();

		this.setupEventHandlers();
		this.setupConnectionEventHandlers();

		await this.start();
	}

	subscribeToNotifications(callback: NotificationCallback): () => void {
		this.notificationSubscribers.add(callback);
		return () => this.notificationSubscribers.delete(callback);
	}

	subscribeToVendingStatus(callback: VendingStatusCallback): () => void {
		this.vendingStatusSubscribers.add(callback);
		return () => this.vendingStatusSubscribers.delete(callback);
	}

	subscribeToConnectionState(callback: ConnectionStateCallback): () => void {
		this.connectionStateSubscribers.add(callback);
		return () => this.connectionStateSubscribers.delete(callback);
	}

	private notifyConnectionStateChange(state: NotificationConnectionState): void {
		this.connectionStateSubscribers.forEach(callback => callback(state));
	}

	private setupEventHandlers(): void {
		if (!this.hubConnection) return;

		this.hubConnection.on("ReceiveNotification", (notification: NotificationPayload) => {
			store.dispatch(notificationReceived(notification));
			this.notificationSubscribers.forEach(callback => callback(notification));
		});

		this.hubConnection.on("BroadcastNotification", (notification: NotificationPayload) => {
			

			// Payload keys may be PascalCase (SubType) or camelCase (subType)
			console.log("BroadcastNotification received:", notification);
			const subType = (notification as any).subType ?? (notification as any).SubType;
			// Popup broadcasts behave like regular notifications (unread counter, list)
			if (Number(subType) === BroadcastNotificationSubType.PopUpNotification) {
				store.dispatch(notificationReceived(notification));
				this.notificationSubscribers.forEach(callback => callback(notification));
			}
			store.dispatch(broadcastNotificationReceived());
		});

		this.hubConnection.on("ReceiveVendingStatusUpdate", (vendingData: VendingStepData) => {
			store.dispatch(vendingStatusReceived(vendingData));
			this.vendingStatusSubscribers.forEach(callback => callback(vendingData));
		});

		this.hubConnection.on("ForceLogout", () => {
			console.warn("🚫 ForceLogout received — account blacklisted");
			store.dispatch(logOut({ userInitiated: false }));
		});
	}

	private setupConnectionEventHandlers(): void {
		if (!this.hubConnection) return;

		this.hubConnection.onclose(error => {
			// console.error("SignalR connection closed:", error);
			store.dispatch(connectionStateChanged(NotificationConnectionState.Disconnected));
			this.notifyConnectionStateChange(NotificationConnectionState.Disconnected);
		});

		this.hubConnection.onreconnecting(error => {
			// console.warn("SignalR reconnecting:", error);
			store.dispatch(connectionStateChanged(NotificationConnectionState.Reconnecting));
			this.notifyConnectionStateChange(NotificationConnectionState.Reconnecting);
		});

		this.hubConnection.onreconnected(connectionId => {
			// console.log("SignalR reconnected:", connectionId);
			this.reconnectAttempts = 0;
			store.dispatch(connectionStateChanged(NotificationConnectionState.Connected));
			this.notifyConnectionStateChange(NotificationConnectionState.Connected);
		});
	}

	private async start(): Promise<void> {
		if (!this.hubConnection || this.stopped) return;

		try {
			store.dispatch(connectionStateChanged(NotificationConnectionState.Connecting));
			this.notifyConnectionStateChange(NotificationConnectionState.Connecting);

			await this.hubConnection.start();
			console.log("✅ SignalR connection established");

			store.dispatch(connectionStateChanged(NotificationConnectionState.Connected));
			this.notifyConnectionStateChange(NotificationConnectionState.Connected);

			this.reconnectAttempts = 0;
		} catch (error) {
			console.error("❌ Error starting SignalR connection:", error);

			store.dispatch(connectionStateChanged(NotificationConnectionState.Failed));
			this.notifyConnectionStateChange(NotificationConnectionState.Failed);

			if (!this.stopped && this.reconnectAttempts < this.maxReconnectAttempts) {
				this.reconnectAttempts++;
				const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
				console.log(`🔄 Retrying connection in ${delay}ms (attempt ${this.reconnectAttempts})`);
				this.retryTimeoutId = setTimeout(() => this.start(), delay);
			}
		}
	}

	async stop(): Promise<void> {
		this.stopped = true;

		// Cancel any pending retry before stopping
		if (this.retryTimeoutId !== null) {
			clearTimeout(this.retryTimeoutId);
			this.retryTimeoutId = null;
		}

		if (this.hubConnection) {
			try {
				await this.hubConnection.stop();
				console.log("🛑 SignalR connection stopped");
				store.dispatch(connectionStateChanged(NotificationConnectionState.Disconnected));
				this.notifyConnectionStateChange(NotificationConnectionState.Disconnected);
			} catch (error) {
				console.error("Error stopping SignalR connection:", error);
			}
			this.hubConnection = null;
			this.reconnectAttempts = 0;
		}
	}

	getConnectionState(): HubConnectionState | null {
		return this.hubConnection?.state ?? null;
	}

	isConnected(): boolean {
		return this.hubConnection?.state === HubConnectionState.Connected;
	}
}

export const notificationHubManager = new NotificationHubManager();
